import api from '@/libs/axiosInstance';
import type { PreparedInvitation } from '@/types/invitation';
import type { ApiResponse, PayloadLocale } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

export const prepareInvitation = async (
  invitationId: string,
  language?: PayloadLocale
): Promise<PreparedInvitation> => {
  const response = await api.get<ApiResponse<PreparedInvitation>>(
    `/dashboard/invitations/${invitationId}/prepare`,
    language ? { params: { language } } : undefined
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to prepare invitation');
  }

  return response.data.data as PreparedInvitation;
};

interface UsePrepareInvitationOptions {
  enabled?: boolean;
  language?: PayloadLocale;
}

export const usePrepareInvitation = (
  invitationId: string,
  options?: UsePrepareInvitationOptions
) => {
  const { enabled = true, language } = options || {};

  return useQuery<PreparedInvitation, Error>({
    queryKey: ['prepareinvitation', invitationId, language ?? null],
    queryFn: () => prepareInvitation(invitationId, language),
    enabled: !!invitationId && enabled,
    gcTime: 1000 * 60 * 5, // 5 minutes
    staleTime: 1000 * 60, // 1 minute
    retry: 2,
    refetchOnWindowFocus: false,
  });
};
